import { createSlice } from '@reduxjs/toolkit';
import { RootState } from 'store';

export type ThemeType = 'light' | 'dark';
export type LanguageType = 'en' | 'pl';

export type ViewType = 'list' | 'grid';

export type ModalItem = {
    name: string;
    props?: Record<string, any>;
};

export interface LayoutState {
    theme: ThemeType;
    language: LanguageType;
    soundOn: boolean;
    projectsView: ViewType;
    projectsReorder: boolean;
    projectAddingTop: boolean;
    projectAddingBottom: boolean;
    modals: ModalItem[];
    searchQuery: string;
}

const getInitialView = (): ViewType => {
    if (typeof window !== 'undefined') {
        const savedView = localStorage.getItem('hustleView');
        return savedView === 'grid' || savedView === 'list'
            ? (savedView as ViewType)
            : 'list';
    }
    return 'list';
};

const getInitialSound = (): boolean => {
    if (typeof window !== 'undefined') {
        const savedSound = localStorage.getItem('hustleSound');
        return savedSound !== 'off';
    }
    return true;
};

const getInitialLanguage = (): LanguageType => {
    if (typeof window !== 'undefined') {
        const saved = localStorage.getItem('hustleLanguage');
        return saved === 'pl' ? 'pl' : 'en';
    }
    return 'en';
};

const initialState: LayoutState = {
    theme: 'light',
    language: getInitialLanguage(),
    soundOn: getInitialSound(),
    projectsView: getInitialView(),
    projectsReorder: false,
    projectAddingBottom: false,
    projectAddingTop: false,
    modals: [],
    searchQuery: '',
};

const layoutSlice = createSlice({
    name: 'layout',
    initialState,
    reducers: {
        setTheme: (state, action) => {
            state.theme = action.payload;
        },
        changeTheme: (state) => {
            state.theme = state.theme === 'light' ? 'dark' : 'light';
        },
        toggleSound: (state) => {
            state.soundOn = !state.soundOn;
        },
        setView: (state, action) => {
            state.projectsView = action.payload;
        },
        changeView: (state) => {
            state.projectsView =
                state.projectsView === 'list' ? 'grid' : 'list';
        },
        setProjectsReorder: (state, action) => {
            state.projectsReorder = action.payload;
        },
        toggleprojectsReorder: (state) => {
            state.projectsReorder = !state.projectsReorder;
        },
        setAddingProjectTop: (state, action) => {
            state.projectAddingTop = action.payload;
        },
        setAddingProjectBottom: (state, action) => {
            state.projectAddingBottom = action.payload;
        },
        openModal: (state, action: { payload: ModalItem }) => {
            const exists = state.modals.find(
                (m) => m.name === action.payload.name
            );
            if (!exists) {
                state.modals.push(action.payload);
            }
        },
        closeModal: (state, action: { payload: { name: string } }) => {
            state.modals = state.modals.filter(
                (m) => m.name !== action.payload.name
            );
        },
        setSearchQuery: (state, action: { payload: string }) => {
            state.searchQuery = action.payload;
        },
        changeLanguage: (state, action: { payload: LanguageType }) => {
            state.language = action.payload;
        },
    },
});

export const {
    setTheme,
    changeTheme,
    toggleSound,
    setView,
    changeView,
    setProjectsReorder,
    toggleprojectsReorder,
    setAddingProjectTop,
    setAddingProjectBottom,
    openModal,
    closeModal,
    setSearchQuery,
    changeLanguage,
} = layoutSlice.actions;

export const selectLayout = (state: RootState) => state.layout;

export const selectModals = (state: RootState) => state.layout.modals;

export const selectModalByName = (name: string) => (state: RootState) =>
    state.layout.modals.find((m) => m.name === name);

export default layoutSlice.reducer;
